/**
 * @Email : kpozos@ibl.mx
 * @Author: Karen Yunuen Pozos Arias
 * @jobTittle: Full Stack Developer
 *
 */

/**
 * Converts a Base64 or Data URL string into a Blob with the given MIME type.
 *
 * @param {string} base64 - The Base64 string or Data URL to convert.
 * @param {string} mime - The MIME type of the Blob (e.g., "image/png", "application/octet-stream").
 * @returns {Blob} A Blob object containing the decoded binary data.
 */
export const base64ToBlob = (base64, mime) => {
    const byteChars = atob(getBase64Content(base64));
    const byteNums = new Array(byteChars.length);

    for (let i = 0; i < byteChars.length; i++) {
        byteNums[i] = byteChars.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNums);
    return new Blob([byteArray], { type: mime });
};

/**
 * Converts a Base64URL-encoded string into a standard Base64 string.
 *
 * @param {string} base64url - The Base64URL string to convert.
 * @returns {string} A valid Base64 string with proper padding.
 */
export function base64urlToBase64(base64url) {
    let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');

    const padding = base64.length % 4;
    if (padding === 2) {
        base64 += '==';
    } else if (padding === 3) {
        base64 += '=';
    }

    return base64;
}

/**
 * Extracts the Base64 content from a string.
 * <p>
 * If the string is a Data URL (contains a comma), this method
 * returns only the part after the comma. Otherwise, it assumes
 * the string is already a pure Base64 string and returns it as-is.
 * </p>
 *
 * @param {string} base64 - The Base64 string or Data URL.
 * @returns {string} The extracted pure Base64 content.
 */
function getBase64Content(base64) {
    if (base64.includes(',')) {
        return base64.split(',')[1];
    }
    return base64;
}

//Converts a base64-encoded string into a File object with the specified filename and MIME type.
export const base64ToFile = (base64, filename) => {
    if (!base64) return null;

    const hasHeader = base64.startsWith('data:');
    const base64Data = hasHeader ? base64.split(',')[1] : base64;

    const mimeMatch = hasHeader
        ? base64.match(/data:(.*?);base64/)
        : null;

    const mime = mimeMatch?.[1] || 'image/jpeg';
    const extension = mime.split('/')[1] || 'jpg';

    try {
        const bstr = atob(base64Data);
        const u8arr = new Uint8Array(bstr.length);

        for (let i = 0; i < bstr.length; i++) {
            u8arr[i] = bstr.charCodeAt(i);
        }

        return new File(
            [u8arr],
            `${filename}.${extension}`,
            { type: mime }
        );
    } catch (e) {
        return null;
    }
};
